import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { AttachmentFolderInstanceService, BuildingEquipmentService } from '@artemis-software/wr-api';
import { FormControl, FormGroup } from '@angular/forms';
import {
  appendSubFolderToValue,
  buildingEquipmentDocumentsDtoToValue,
  buildingEquipmentDocumentsValueToMergeDto,
  folderInstanceDtosToFolderInstances,
  removeSubFolderFromValue,
  renameSubFolderInValue,
} from '@shared/form-controls/attachment-folder-tree/attachment-folder-utils';
import {
  AttachmentFolderInstance,
  BuildingDocumentsValue,
} from '@shared/form-controls/attachment-folder-tree/attachment-folder-tree.component';
import { SubFolderDialogComponent } from '@components/dialog/sub-folder-dialog/sub-folder-dialog.component';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { isContractorTenant } from '@/utils/admin-utils';
import { CurrentUserService } from '@services/current-user.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';

@Component({
  standalone: false,
  selector: 'wr-building-equipment-folders-tab',
  templateUrl: './building-equipment-folders-tab.component.html',
  styleUrl: './building-equipment-folders-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuildingEquipmentFoldersTabComponent {
  readonly buildingEquipmentService = inject(BuildingEquipmentService);
  readonly attachmentFolderInstanceService = inject(AttachmentFolderInstanceService);
  readonly currentUserService = inject(CurrentUserService);
  readonly toastrService = inject(NbToastrService);
  readonly dialogService = inject(NbDialogService);

  readonly canEditFolderContents = computed(() => this.currentUserService.hasPrivilege('editAttachmentFolderContents'));
  readonly isContractor = isContractorTenant();

  attachmentForm!: FormGroup;

  saving$ = new BehaviorSubject(false);

  readonly buildingEquipmentId = input.required<string>();
  readonly isEditing = input(false);

  readonly reloadCounter = signal(0);

  readonly folderInstancesDto = asyncComputed(() => {
    this.reloadCounter();
    const buildingEquipmentId = this.buildingEquipmentId();
    return this.buildingEquipmentService.getAttachmentFolderInstancesByBuildingEquipment(buildingEquipmentId);
  });

  constructor() {
    effect(() => {
      const dto = this.folderInstancesDto();
      if (dto) {
        this.attachmentForm = new FormGroup({
          id: new FormControl(dto.id),
          attachmentFolderInstances: new FormControl(buildingEquipmentDocumentsDtoToValue(dto)),
        });
        untracked(() => this.applyEditState());
      }
    });
    effect(() => {
      this.isEditing();
      this.canEditFolderContents();
      this.applyEditState();
    });
  }

  private applyEditState(): void {
    if (!this.attachmentForm) {
      return;
    }
    if (this.isEditing() && this.canEditFolderContents()) {
      this.attachmentForm.enable();
    } else {
      this.attachmentForm.disable();
    }
  }

  hasPendingChanges(): boolean {
    return this.isEditing();
  }

  async submit(): Promise<boolean> {
    try {
      this.saving$.next(true);
      const value = this.attachmentForm?.value.attachmentFolderInstances as BuildingDocumentsValue;
      await firstValueFrom(this.buildingEquipmentService.mergeAttachmentFolderInstances(
        buildingEquipmentDocumentsValueToMergeDto(this.attachmentForm?.value.id, value),
      ));
      this.reloadCounter.update((counter) => counter + 1);
      this.toastrService.success('Dateien wurden erfolgreich gespeichert', 'TGA');
      return true;
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Dateien konnten nicht gespeichert werden.', 'Fehler');
      return false;
    } finally {
      this.saving$.next(false);
    }
  }

  cancel(): void {
    this.reloadCounter.update((counter) => counter + 1);
  }

  private patchDocumentsValue(
    mutate: (value: BuildingDocumentsValue) => BuildingDocumentsValue,
  ): void {
    const control = this.attachmentForm.get('attachmentFolderInstances')!;
    control.setValue(mutate(control.value as BuildingDocumentsValue));
  }

  async openCreateSubFolderDialog(parent: AttachmentFolderInstance): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner anlegen' },
    }).onClose);
    if (!result) {
      return;
    }
    try {
      this.saving$.next(true);
      const created = await firstValueFrom(this.attachmentFolderInstanceService.merge({
        parentId: parent.id,
        name: result.name,
        color: result.color,
      }));
      const createdInstance = folderInstanceDtosToFolderInstances([created])[0];
      this.patchDocumentsValue((value) => appendSubFolderToValue(value, parent.id!, createdInstance));
      this.toastrService.success('Unterordner wurde erfolgreich angelegt', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht angelegt werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  async openRenameSubFolderDialog(subFolder: AttachmentFolderInstance): Promise<void> {
    const result = await firstValueFrom(this.dialogService.open(SubFolderDialogComponent, {
      context: { header: 'Unterordner bearbeiten', name: subFolder.name, color: subFolder.color },
    }).onClose);
    if (!result) {
      return;
    }
    try {
      this.saving$.next(true);
      await firstValueFrom(this.attachmentFolderInstanceService.merge({
        id: subFolder.id,
        name: result.name,
        color: result.color,
      }));
      this.patchDocumentsValue((value) => renameSubFolderInValue(value, subFolder, result.name, result.color));
      this.toastrService.success('Unterordner wurde erfolgreich gespeichert', 'Ordner');
    } catch (e) {
      console.error(e);
      this.toastrService.danger('Unterordner konnte nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving$.next(false);
    }
  }

  confirmDeleteSubFolder(subFolder: AttachmentFolderInstance): void {
    this.dialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Unterordner löschen?',
        message: `Der Unterordner "${subFolder.name}" und alle enthaltenen Dokumente werden gelöscht.`,
      },
    }).onClose.subscribe({
      next: async (result) => {
        if (!result) {
          return;
        }
        try {
          this.saving$.next(true);
          await firstValueFrom(this.attachmentFolderInstanceService.deleteById(subFolder.id!));
          this.patchDocumentsValue((value) => removeSubFolderFromValue(value, subFolder));
          this.toastrService.success('Unterordner wurde erfolgreich gelöscht', 'Ordner');
        } catch (e) {
          console.error(e);
          this.toastrService.danger('Unterordner konnte nicht gelöscht werden.', 'Fehler');
        } finally {
          this.saving$.next(false);
        }
      },
    });
  }
}
